"use client";

/* eslint-disable indent */
import React, { useMemo } from "react";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { debounce } from "lodash";
import { useRouter } from "next/router";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { GalleryImage } from "components/atoms/GalleryImage";
import ActionFolderModal from "components/molecules/ActionFolderModal";
import { DeleteAllConfirmationModal } from "components/molecules/DeleteAllModal";
import { DeleteConfirmationModal } from "components/molecules/DeleteModal";
import EligibleCompetitionsModal from "components/molecules/EligibleCompetitionsModal";
import SearchAccordion from "components/molecules/SearchAccordion";
import ShareModal from "components/molecules/ShareModal";
import { addImagesToFolder, deleteAllImages, deleteImage, getFilterFields, moveImagesToFolder, onToggleVisibility, removeImagesFromFolder, searchImages, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, FilterFieldDTO, IDeleteImageRequest, ImageDTO, ImageSearchQueryDTO, UserPremiumType } from "types";
import { Label, SubTitle } from "utils/styling/typography.styled";
import * as Styled from "./ImageGallery.styled";
const LightboxWithMetadata = dynamic(() => import("components/molecules/Lightbox").then(module => ({
  default: module.LightboxWithMetadata,
  ssr: false,
  loading: () => <Skeleton className="h-full w-full" />
})));
import dynamic from "next/dynamic";
import { Skeleton } from "@/components/ui/skeleton";
import Masonry from "react-masonry-css";
type ImageGalleryProps = {
  gallerySubtitle: string;
  userGallery: boolean;
  defaultFilters?: {
    [key: string]: string;
  };
  folderId?: string | null;
};
const ImageGallery: FC<ImageGalleryProps> = ({
  gallerySubtitle,
  userGallery,
  defaultFilters,
  folderId
}) => {
  // Selection logic
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showDeleteAllConfirmation, setShowDeleteAllConfirmation] = useState(false);

  // Lightbox / Detailed view
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);

  // App context & dispatch
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const router = useRouter();

  // Single-image delete
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  // Sorting & filtering states
  const [sort, setSort] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [filterFields, setFilterFields] = useState<FilterFieldDTO[]>([]);
  const [filters, setFilters] = useState<any>(defaultFilters ?? {});
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Paged-based pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(state.user?.defaultPageSize || 30);

  // Share
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareImageInfo, setShareImageInfo] = useState<ImageDTO | null>(null);

  // Competitions
  const [showEligibleCompetitionsModal, setShowEligibleCompetitionsModal] = useState(false);
  const [currentImageId, setCurrentImageId] = useState<string | null>(null);

  // Folder Move
  const [targetFolderId, setTargetFolderId] = useState<string | null>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRemoveFromFolderModal, setShowRemoveFromFolderModal] = useState(false);
  const [removeFolder, setRemoveFolder] = useState<string | null>(null);
  const [isMove, setIsMove] = useState<boolean>(false);
  const [sourceFolderId, setSourceFolderId] = useState<string | null>(folderId ?? null);

  // For error handling
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch images with standard pagination
  const fetchPaginatedImages = async () => {
    try {
      const searchQuery: ImageSearchQueryDTO = {
        query: debouncedQuery,
        sortField: sort,
        sortOrder,
        filters,
        page: currentPage,
        pageSize,
        userGallery
      };
      const response = await searchImages(searchQuery);
      if (response.error) {
        setError(typeof response.error === "string" ? response.error : response.error.message);
        return {
          error: response.error
        };
      }
      return response;
    } catch (e: any) {
      captureException(e);
      setError("Failed to get images");
      return {
        error: "Failed to get images"
      };
    }
  };
  const {
    data,
    isLoading,
    isError: isQueryError
  } = useQuery(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], fetchPaginatedImages, {
    keepPreviousData: true,
    staleTime: 30000 // 30 seconds
  });
  const images = data?.images ?? [];
  const totalImages = data?.totalCount ?? 0;

  // Pagination
  const totalPages = Math.ceil(totalImages / pageSize);

  // Debounce for the search bar
  const debouncedSetQuery = useMemo(() => debounce((value: string) => {
    setDebouncedQuery(value);
  }, 500), []);
  useEffect(() => {
    return () => {
      debouncedSetQuery.cancel();
    };
  }, [debouncedSetQuery]);
  useEffect(() => {
    setFilters(defaultFilters || {});
    // Reset page or do other adjustments if needed
    setCurrentPage(0);
  }, [defaultFilters]);
  const handleQueryChange = (value: React.SetStateAction<string>) => {
    setQuery(value);
    const newValue = typeof value === "function" ? value(query) : value;
    debouncedSetQuery(newValue);
  };

  // Folder logic
  const openMoveModal = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (folderId) {
      setIsMove(true);
      setSourceFolderId(folderId);
      setShowMoveModal(true);
    } else {
      setError("Source folder ID is not available.");
    }
  };
  const openAddModal = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    setIsMove(false);
    setShowMoveModal(true);
  };
  const handleMoveImages = async (folderId: string) => {
    if (!sourceFolderId) {
      setError("Source folder ID is missing.");
      return;
    }
    try {
      const result = await moveImagesToFolder(folderId, sourceFolderId, Array.from(selectedImages));
      if (result.success) {
        queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
          if (!oldData || !oldData.images) return oldData;
          // Remove the deleted image from oldData.images
          const newImages = oldData.images.filter((img: ImageDTO) => !selectedImages.has(img.id));
          return {
            ...oldData,
            images: newImages,
            totalCount: oldData.totalCount - selectedImages.size
          };
        });
        handleDeselectAll();
        setShowMoveModal(false);
      } else {
        setError(result.error || "Failed to move images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to move images");
    }
  };
  const handleAddImagesToFolder = async (folderId: string) => {
    try {
      const result = await addImagesToFolder(folderId, Array.from(selectedImages));
      if (result.success) {
        handleDeselectAll();
        setShowMoveModal(false);
      } else {
        setError(result.error || "Failed to add images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to add images");
    }
  };
  const handleRemoveImagesFromFolder = async (folderId: string) => {
    setRemoveFolder(folderId);
    setShowMoveModal(false);
    setShowRemoveFromFolderModal(true);
  };
  const handleSubmitRemoveImagesFromFolder = async () => {
    if (!removeFolder) return;
    try {
      const result = await removeImagesFromFolder(removeFolder, Array.from(selectedImages));
      if (result.success) {
        queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
          if (!oldData || !oldData.images) return oldData;
          // Remove the deleted image from oldData.images
          const newImages = oldData.images.filter((img: ImageDTO) => !selectedImages.has(img.id));
          return {
            ...oldData,
            images: newImages,
            totalCount: oldData.totalCount - selectedImages.size
          };
        });
        handleDeselectAll();
        setShowRemoveFromFolderModal(false);
      } else {
        setError(result.error || "Failed to remove images");
      }
    } catch (err: any) {
      setError(err.message || "Failed to remove images");
    }
  };
  const handleSetSelectedFolderId = (folderId: string | null) => {
    setTargetFolderId(folderId);
  };
  const handleUpscalerModalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      const editedImage = images.find(image => image.id === imageId);
      if (editedImage && editedImage.imageMetadata.npcFieldsMap["Edit Type"] === "UPSCALER") {
        toast.error("This image has already been upscaled and cannot be upscaled again.");
      } else {
        router.push({
          pathname: "/edit",
          query: {
            editMode: "Upscale Image",
            imageId: imageId
          }
        });
      }
    }
  };
  const handleInpaintClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageId
        }
      });
    }
  };
  const handleBGRemovalClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Remove Background",
          imageId: imageId
        }
      });
    }
  };
  const handleAudioClick = (imageId?: string) => {
    if (imageId) {
      if (imageId) {
        router.push({
          pathname: "/edit",
          query: {
            editMode: "Apply Audio",
            imageId: imageId
          }
        });
      }
    }
  };
  const handleFaceSwapClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Face Swap",
          imageId: imageId
        }
      });
    }
  };
  const handleADetailerClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          imageId: imageId
        }
      });
    }
  };
  const handleEditClick = (imageId?: string) => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    if (imageId) {
      router.push({
        pathname: "/edit",
        query: {
          editMode: "Spot Heal",
          imageId: imageId
        }
      });
    }
  };
  const handleCloseEligibleCompetitionsModal = () => {
    setShowEligibleCompetitionsModal(false);
    setCurrentImageId(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };

  // Share
  const handleShareClick = (e: React.MouseEvent, imageId?: string) => {
    e.preventDefault();
    e.stopPropagation();
    const selectedImage = images.find(image => image.id === imageId);
    if (selectedImage) {
      setShareImageInfo(selectedImage);
      setIsShareModalOpen(true);
    }
  };
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  // Filter fields
  useEffect(() => {
    const fetchFilters = async () => {
      const fields = await getFilterFields();
      setFilterFields(fields);
    };
    fetchFilters();
  }, []);

  // Lightbox
  const handleImageClick = useCallback((image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  }, []);
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(images[newIndex]);
  };
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  };

  // Selection
  const handleSelectImage = async (imageId: string) => {
    setIsSelectionMode(true);
    setSelectedImages(prev => {
      const newSelectedImages = new Set(prev);
      if (newSelectedImages.has(imageId)) {
        newSelectedImages.delete(imageId);
      } else {
        newSelectedImages.add(imageId);
      }
      return newSelectedImages;
    });
  };
  const handleDeselectAll = () => {
    setIsSelectionMode(false);
    setError("");
    setSelectedImages(new Set());
  };

  // Deleting multiple
  const handleDeleteAll = async () => {
    try {
      const dataPayload: IDeleteImageRequest = {
        imageIds: Array.from(selectedImages)
      };
      await deleteAllImages(dataPayload);
      queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
        if (!oldData || !oldData.images) return oldData;
        // Remove the deleted image from oldData.images
        const newImages = oldData.images.filter((img: ImageDTO) => !selectedImages.has(img.id));
        return {
          ...oldData,
          images: newImages,
          totalCount: oldData.totalCount - selectedImages.size
        };
      });
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    } catch (error: any) {
      captureException("Failed to delete images", error);
      setShowDeleteAllConfirmation(false);
      handleDeselectAll();
    }
  };

  // Re-fetch any time we reset or change our inputs
  useEffect(() => {
    // Reset to page 0 whenever these changes happen
    setCurrentPage(0);
  }, [sort, sortOrder, filters, debouncedQuery, pageSize]);

  // Voting
  const handleVote = async (imageId: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.votedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
        if (!oldData?.images) return oldData;
        // Map to update the single image
        const newImages = oldData.images.map((img: ImageDTO) => img.id === imageId ? {
          ...img,
          votedByUser: newVoteStatus,
          voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
        } : img);
        return {
          ...oldData,
          images: newImages
        };
      });
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            votedByUser: newVoteStatus,
            voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
          };
        });
      }
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };

  // Single-image delete
  const prepareForDelete = (imageId: string) => {
    setImageToDelete(imageId);
    setShowDeleteConfirmation(true);
  };
  const handleConfirmDelete = async (imageId?: string) => {
    if (!imageToDelete && !imageId) return;
    try {
      let newImageDtos: ImageDTO[] | undefined;
      const targetId = imageToDelete || imageId;
      if (targetId) {
        await deleteImage(targetId);
        newImageDtos = images.filter(img => img.id !== targetId);
      }
      if (!newImageDtos) return;
      queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
        if (!oldData || !oldData.images) return oldData;
        // Remove the deleted image from oldData.images
        const newImages = oldData.images.filter((img: ImageDTO) => img.id !== imageId);
        return {
          ...oldData,
          images: newImages,
          totalCount: oldData.totalCount - 1
        };
      });
      queryClient.invalidateQueries(["images", userGallery]);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    } catch (error: any) {
      captureException("Failed to delete image", error);
      setShowDeleteConfirmation(false);
      setImageToDelete(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setImageToDelete(null);
  };
  const handleCompetitionsClickMemo = useCallback((imageId?: string) => {
    if (!imageId) return;
    setCurrentImageId(imageId);
    setShowEligibleCompetitionsModal(true);
  }, []);

  // Visibility toggle
  const isUserImage = (imageUsername: string) => state.username === imageUsername;
  const handleToggle = (imageId: string) => {
    const currentVisibility = images.find(img => img.id === imageId)?.public;
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      queryClient.setQueryData(["images", currentPage, sort, sortOrder, filters, debouncedQuery, pageSize], (oldData: any) => {
        if (!oldData?.images) return oldData;
        // Map to update the single image
        const newImages = oldData.images.map((img: ImageDTO) => img.id === imageId ? {
          ...img,
          public: newVisibility
        } : img);
        return {
          ...oldData,
          images: newImages
        };
      });
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };

  // For the masonry layout
  const breakpointColumnsObj = {
    default: 6,
    1440: 4,
    1024: 3,
    768: 2,
    576: 1
  };

  // Render each image in a masonry grid
  const renderImage = (image: ImageDTO) => <GalleryImage key={image.id} item={image} isUserImage={isUserImage} onImageClick={handleImageClick} handleToggle={handleToggle} handleEditClick={handleEditClick} handleADetailerClick={handleADetailerClick} handleFaceSwapClick={handleFaceSwapClick} handleBGRemovalClick={handleBGRemovalClick} handleInpaintClick={handleInpaintClick} handleUpscaleClick={handleUpscalerModalClick} handleAudioClick={handleAudioClick} prepareForDelete={prepareForDelete} handleVote={handleVote} isSelectionMode={isSelectionMode} handleSelectImage={handleSelectImage} isSelected={selectedImages.has(image.id)} userGallery={userGallery} handleShareClick={handleShareClick} handleCompetitionsClick={handleCompetitionsClickMemo} showDeleteConfirmation={showDeleteConfirmation} data-sentry-element="GalleryImage" data-sentry-component="renderImage" data-sentry-source-file="index.tsx" />;
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleChangePageSize = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };
  return <Styled.ImageGalleryWrapper suppressHydrationWarning data-sentry-element="unknown" data-sentry-component="ImageGallery" data-sentry-source-file="index.tsx">
            <SubTitle data-sentry-element="SubTitle" data-sentry-source-file="index.tsx">{gallerySubtitle}</SubTitle>
            <SearchAccordion filterFields={filterFields} filters={filters} handleFilterChange={event => {
      const {
        name,
        value,
        type
      } = event.target;
      const checked = type === "checkbox" ? (event.target as HTMLInputElement).checked : false;
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [name]: type === "checkbox" ? checked : value
      }));
    }} query={query} setQuery={handleQueryChange} sort={sort} setSort={setSort} sortOrder={sortOrder} setSortOrder={setSortOrder} pageSize={pageSize} handleChangePageSize={handleChangePageSize} currentPage={currentPage} totalPages={totalPages} handlePrevPage={handlePrevPage} handleNextPage={handleNextPage} data-sentry-element="SearchAccordion" data-sentry-source-file="index.tsx">
                {userGallery && <>
                        {!isSelectionMode && images.length > 0 && <Button variant={ButtonType.PRIMARY} onClick={() => setIsSelectionMode(true)} className="mb-4">
                                Enable Selection Mode
                            </Button>}
                        {isSelectionMode && <Styled.ButtonContainer>
                                {folderId && <Button variant={ButtonType.PRIMARY} onClick={() => openMoveModal()} disabled={selectedImages.size === 0 || !folderId}>
                                        Move Selected to Folder
                                    </Button>}
                                <Button variant={ButtonType.PRIMARY} onClick={() => openAddModal()} disabled={selectedImages.size === 0}>
                                    Add Selected to Folder
                                </Button>
                                {folderId && <Button variant={ButtonType.PRIMARY} onClick={() => handleRemoveImagesFromFolder(folderId)} disabled={selectedImages.size === 0 || !folderId}>
                                        Remove Selected from Folder
                                    </Button>}
                                <Button variant={ButtonType.PRIMARY} onClick={() => setShowDeleteAllConfirmation(true)}>
                                    Delete Selected Images
                                </Button>
                                <Button variant={ButtonType.ERROR} onClick={handleDeselectAll}>
                                    Cancel
                                </Button>
                            </Styled.ButtonContainer>}
                    </>}
                {error && <div>{error}</div>}
                {isLoading && <Label>Loading ...</Label>}
                {isQueryError && <Label>Error loading images.</Label>}
                {!isLoading && !isQueryError && images.length === 0 && <Label>No images to display</Label>}

                {/* The Masonry layout */}
                {images.length > 0 && <Masonry breakpointCols={breakpointColumnsObj} className="flex gap-4" columnClassName="bg-clip-padding align-center">
                        {images.map(renderImage)}
                    </Masonry>}
            </SearchAccordion>

            {/* Lightbox for full view */}
            <LightboxWithMetadata imageDtos={images} initialImageId={selectedImage?.id} onClose={handleLightboxClose} open={isLightboxOpen} onToggleVisibility={handleToggle} onVote={handleVote} handleConfirmDelete={handleConfirmDelete} onIndexChange={handleIndexChange} data-sentry-element="LightboxWithMetadata" data-sentry-source-file="index.tsx" />

            {/* Modals */}
            <DeleteConfirmationModal isOpen={showDeleteConfirmation} onConfirm={handleConfirmDelete} onCancel={handleCancelDelete} text="image" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <DeleteConfirmationModal isOpen={showRemoveFromFolderModal} onConfirm={handleSubmitRemoveImagesFromFolder} onCancel={() => setShowRemoveFromFolderModal(false)} text="images from folder" data-sentry-element="DeleteConfirmationModal" data-sentry-source-file="index.tsx" />
            <DeleteAllConfirmationModal isOpen={showDeleteAllConfirmation} onConfirm={handleDeleteAll} onCancel={() => setShowDeleteAllConfirmation(false)} deleteCount={selectedImages.size} data-sentry-element="DeleteAllConfirmationModal" data-sentry-source-file="index.tsx" />
            <ActionFolderModal open={showMoveModal} onClose={() => setShowMoveModal(false)} onAction={isMove ? handleMoveImages : handleAddImagesToFolder} selectedFolderId={targetFolderId} setSelectedFolderId={handleSetSelectedFolderId} text={isMove ? "Move Images to Folder" : "Add Images to Folder"} data-sentry-element="ActionFolderModal" data-sentry-source-file="index.tsx" />
            {shareImageInfo && <ShareModal isOpen={isShareModalOpen} image={shareImageInfo} onClose={handleCloseShareModal} />}
            {currentImageId && <EligibleCompetitionsModal isOpen={showEligibleCompetitionsModal} onCancel={handleCloseEligibleCompetitionsModal} imageId={currentImageId} />}
        </Styled.ImageGalleryWrapper>;
};
export default React.memo(ImageGallery);